import { OfferForm } from "./offer.schema";
import { useFormContext } from "react-hook-form";
import { Card } from "@/src/components/wrappers/Card";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Check, CopyIcon, ExternalLink, Plus, Sparkles } from "lucide-react";
import { useStepsStore } from "./state";
import { useToast } from "../../../components/ui/use-toast";
import { copyToClipboard } from "../../../lib/utils";
import { useOffersStore } from "../../../stores/offersStore";
import { Offer, OfferYacht } from "../model";
import { YachtDetailsModal } from "./YachtDetailsModal";
import { ActionButton } from "../../../components/wrappers/WithActionBar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/src/components/ui/dialog";
import { fetchAvailability, Availability } from "./useOldSearch";
import { fetch } from "@/src/lib/fetch";
import { useEmit } from "@/src/lib/actions";

export function Summary({
  dealLink,
  openChangeDetails,
  offerId,
}: {
  dealLink?: Offer["dealLink"];
  openChangeDetails: (id: string, index: number) => void;
  offerId?: string;
}) {
  const [yachtDetailsOpen, setYachtDetailsOpen] = useState(false);
  const [yachtId, setYachtId] = useState<string | null>(null);
  const [url, setUrl] = useState("");
  const [expandedExtras, setExpandedExtras] = useState<Record<number, boolean>>({});
  const [ratesFor, setRatesFor] = useState<OfferYacht | null>(null);
  const [holding, setHolding] = useState<number | null>(null);
  const [drafting, setDrafting] = useState<string | number | null>(null);
  const emitPreviewSave = useEmit<undefined>("offer:previewSave");
  const values = useFormContext<OfferForm>().getValues();
  const { setValue } = useFormContext<OfferForm>();
  const [intro, setIntro] = useState<string>(values.description?.intro ?? "");
  const steps = useStepsStore();
  const { offerYachts, setOfferYachts, currencies } = useOffersStore();

  const { toast } = useToast();

  useEffect(() => {
    const origin = window.location.href.replace("/offers/add", "/offer/id_HERE");
    setUrl(origin);
  }, []);

  const handleCopyToClipboard = (text: string) => {
    copyToClipboard(text);
    toast({
      title: "Copied!",
      variant: "default",
    });
  };

  const handleYachtDetailsModal = (id: string) => {
    setYachtId(id);
    setYachtDetailsOpen(true);
  };

  const moveYacht = (from: number, to: number) => {
    const sorted = [...offerYachts].sort((a, b) => a.order - b.order);

    if (to < 0 || to >= sorted.length) return;

    const updated = [...sorted];
    [updated[from], updated[to]] = [updated[to], updated[from]];

    const reordered = updated.map((yacht, index) => ({
      ...yacht,
      order: index,
    }));

    setOfferYachts(reordered);
    setValue("yachts", reordered);
  };

  const SKIPPER_KEY = "optional-skipper";
  // A real skipper service in the yacht's own extras (e.g. "Skipper (+ meals)"),
  // not a deposit/waiver line that merely mentions "skipper".
  const SKIPPER_RE = /\bskippers?\b/i;
  const isSkipperExtra = (a: any) =>
    SKIPPER_RE.test(a?.text ?? "") && !/deposit|waiver|insur|licen/i.test(a?.text ?? "");
  // Pick the yacht's skipper extra — prefer a priced one over a €0 placeholder.
  const findSkipperIdx = (amen: any[]) => {
    const priced = amen.findIndex((a) => isSkipperExtra(a) && (a?.price ?? 0) > 0);
    return priced >= 0 ? priced : amen.findIndex(isSkipperExtra);
  };
  const someHasSkipper = offerYachts.some((y: any) =>
    (y.amenitiesDetails ?? []).some((a: any) => isSkipperExtra(a) && a.checked),
  );
  const toggleSkipperAll = () => {
    const next = !someHasSkipper;
    const updated = offerYachts.map((y: any) => {
      const amen = [...((y.amenitiesDetails as any[]) ?? [])];
      const idx = findSkipperIdx(amen);
      // Drop any legacy blank "Skipper (optional)" custom line we used to inject.
      const customAmenities = ((y.customAmenities as any[]) ?? []).filter((a: any) => a.dedupeKey !== SKIPPER_KEY);
      if (idx < 0) return { ...y, customAmenities }; // no skipper extra on this yacht
      const a = amen[idx];
      if (!!a.checked === next) return { ...y, customAmenities };
      amen[idx] = { ...a, checked: next };
      // Advance-paid skipper moves the advance total; a locally-paid one doesn't.
      const p = y.pricing ?? {};
      const delta = a.paidLocally ? 0 : next ? a.price ?? 0 : -(a.price ?? 0);
      return { ...y, amenitiesDetails: amen, customAmenities, pricing: { ...p, totalPrice: (p.totalPrice ?? 0) + delta } };
    });
    setOfferYachts(updated as any);
    setValue("yachts", updated as any);
  };

  // Bulk "5% off the charter fee" across every option. Toggle: applying stores each
  // yacht's current charter price so removing restores it exactly (not cur/0.95,
  // which rounding would drift). Single batched setOfferYachts — a per-yacht loop of
  // updateLine would read stale state and only land the last change.
  const [discountAllApplied, setDiscountAllApplied] = useState(false);
  const [preDiscountPrices, setPreDiscountPrices] = useState<Record<number, number> | null>(null);
  const toggleDiscountAll = () => {
    if (discountAllApplied && preDiscountPrices) {
      const restored = offerYachts.map((y: any, i: number) => {
        const orig = preDiscountPrices[i];
        if (orig == null) return y;
        const p = y.pricing ?? {};
        const cur = p.clientPrice ?? p.totalPrice ?? 0;
        return { ...y, pricing: { ...p, clientPrice: orig, totalPrice: (p.totalPrice ?? cur) + (orig - cur) } };
      });
      setOfferYachts(restored as any);
      setValue("yachts", restored as any);
      setPreDiscountPrices(null);
      setDiscountAllApplied(false);
      return;
    }
    const originals: Record<number, number> = {};
    const discounted = offerYachts.map((y: any, i: number) => {
      const p = y.pricing ?? {};
      const cur = p.clientPrice ?? p.totalPrice ?? 0;
      originals[i] = cur;
      const val = Math.round(cur * 0.95);
      return { ...y, pricing: { ...p, clientPrice: val, totalPrice: (p.totalPrice ?? cur) + (val - cur) } };
    });
    setOfferYachts(discounted as any);
    setValue("yachts", discounted as any);
    setPreDiscountPrices(originals);
    setDiscountAllApplied(true);
  };

  const removeLine = (index: number) => {
    const sorted = [...offerYachts].sort((a, b) => a.order - b.order);
    const target = sorted[index];
    if (!target) return;
    const updated = offerYachts.filter((y) => y !== target).map((y, i2) => ({ ...y, order: i2 }));
    setOfferYachts(updated as any);
    setValue("yachts", updated as any);
  };

  const updateLine = (index: number, patch: Record<string, unknown>) => {
    const sorted = [...offerYachts].sort((a, b) => a.order - b.order);
    const target = sorted[index];
    if (!target) return;
    const updated = offerYachts.map((y) => (y === target ? { ...y, ...patch } : y));
    setOfferYachts(updated as any);
    setValue("yachts", updated as any);
  };

  const putOnHold = async (index: number, oy: any) => {
    const startDate = oy.startDate;
    const endDate = oy.endDate;
    if (!startDate || !endDate || !oy.yacht?.id) {
      toast({ title: "Can't put on hold", description: "Yacht is missing charter dates.", variant: "destructive" });
      return;
    }
    setHolding(index);
    try {
      const periodFrom = `${startDate}T${oy.startTime || "12:00"}:00Z`;
      const periodTo = `${endDate}T${oy.endTime || "12:00"}:00Z`;
      const res = await fetch<{ data: { expirationDate?: string } }>(`/api/options/options`, "POST", {
        yachtId: oy.yacht.id,
        createInApi: true,
        periodFrom,
        periodTo,
      });
      const expiry = res?.data?.expirationDate ?? null;
      updateLine(index, {
        yachtOnHold: true,
        holdExpiresAt: expiry ? String(expiry).slice(0, 10) : (oy.holdExpiresAt ?? null),
      });
      toast({
        title: "Yacht on hold",
        description: expiry ? `Held for the client until ${new Date(expiry).toLocaleDateString("en-GB")}` : undefined,
      });
    } catch (e: any) {
      toast({ title: "Couldn't put on hold", description: e?.message, variant: "destructive" });
    } finally {
      setHolding(null);
    }
  };

  // Quick-edit the client-facing charter price on the Summary; keeps the total
  // in step by shifting it by the same delta (extras/VAT unchanged).
  const setCustomerPrice = (index: number, pricing: any, val: number) => {
    const oldClient = pricing.clientPrice ?? pricing.totalPrice ?? 0;
    const delta = val - oldClient;
    updateLine(index, {
      pricing: { ...pricing, clientPrice: val, totalPrice: (pricing.totalPrice ?? oldClient) + delta },
    });
  };

  const sortedOfferYachts = useMemo(() => {
    return [...offerYachts].sort((a, b) => a.order - b.order);
  }, [offerYachts]);

  // AI drafting (Claude, HELM tone) — the covering message and per-yacht callouts.
  const draftAi = async (kind: "message" | "yacht", index?: number, oy?: any) => {
    setDrafting(kind === "message" ? "message" : (index ?? -1));
    try {
      const d: any = values.description ?? {};
      const sorted = [...offerYachts].sort((a, b) => a.order - b.order);
      const first: any = sorted[0];
      const fmtPrice = (n?: number) =>
        new Intl.NumberFormat("en-EN", {
          style: "currency",
          currency: d.currency ?? "EUR",
          maximumFractionDigits: 0,
        }).format(n ?? 0);
      const CREWED = ["CYA", "Yachtfolio", "Helm", "Ankor"];
      // Map an offer line to the AI's yacht context — the specific signals it needs to
      // say something true (systems, toys, crew, charter basis) rather than generic copy.
      const toYachtCtx = (line: any) => {
        const yy: any = line.yacht ?? {};
        const p: any = line.pricing ?? {};
        const crewed = CREWED.includes(yy.provider);
        const inclusive =
          p.charterTerms === "inclusive" ||
          yy.meta?.cyaPricing?.inclusive === true ||
          yy.meta?.yfInfo?.inclusive === true ||
          (crewed && !p.vatPrice && !p.apaPrice);
        return {
          name: yy.name,
          builder: yy.builder,
          boatType: yy.boatType,
          cabins: yy.cabins,
          maxPeople: yy.maxPeople,
          crew: yy.crewNumber,
          operator: yy.operator?.name ?? yy.operator,
          charterType: crewed ? "crewed" : d.charterType || "bareboat",
          inclusive,
          features: (line.amenitiesDetails ?? [])
            .filter((a: any) => (a.checked || a.obligatory) && !a.hidden && a.text)
            .map((a: any) => a.text)
            .slice(0, 8),
          price: fmtPrice(p.clientPrice ?? p.totalPrice),
        };
      };
      const payload: any = {
        kind,
        offer: {
          clientName: values.customer?.firstName,
          region: (d.title || "").replace(/ yacht charter.*/i, "").trim() || undefined,
          checkIn: first?.startDate,
          checkOut: first?.endDate,
          guests: d.guests,
          charterType: d.charterType,
          brand: d.brand,
          // The covering note compares the shortlist, so send every option.
          ...(kind === "message" ? { yachts: sorted.map(toYachtCtx) } : {}),
        },
      };
      if (kind === "yacht" && oy) {
        payload.yacht = toYachtCtx(oy);
      }
      const res = await fetch<{ text?: string; error?: string }>("/api/ai/draft", "POST", payload);
      if (res?.text) {
        if (kind === "message") {
          setIntro(res.text);
          setValue("description.intro", res.text, { shouldDirty: true });
        } else if (index !== undefined) {
          updateLine(index, { brokerNote: res.text });
        }
      } else {
        toast({ title: "Couldn't draft", description: res?.error, variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Couldn't draft", description: e?.message, variant: "destructive" });
    } finally {
      setDrafting(null);
    }
  };

  const firstName = values.customer?.firstName?.trim();
  const heldCount = sortedOfferYachts.filter((y: any) => y.holdExpiresAt).length;

  return (
    <div className="grid xl:grid-cols-[1fr_340px] gap-6 items-start">
    <div className="flex flex-col gap-6 min-w-0">
      <Card>
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-lg font-semibold">Customer details</h3>
          <Button onClick={() => steps.setStep("Customer")} variant="link" size="sm">
            Edit
          </Button>
        </div>
        <div className="grid grid-cols-4 gap-6">
          <div className="flex flex-col gap-2 min-w-0">
            <span className="text-base font-normal">Name</span>
            <span className="text-base font-medium truncate">{values.customer?.firstName}</span>
          </div>
          <div className="flex flex-col gap-2 min-w-0">
            <span className="text-base font-normal">Surname</span>
            <span className="text-base font-medium truncate">{values.customer?.lastName}</span>
          </div>
          <div className="flex flex-col gap-2 min-w-0">
            <span className="text-base font-normal">E-mail</span>
            <span className="text-base font-medium truncate" title={values.customer?.email}>
              {values.customer?.email}
            </span>
          </div>
          <div className="flex flex-col gap-2 min-w-0">
            <span className="text-base font-normal">Phone number</span>
            <span className="text-base font-medium truncate">{values.customer?.mobile}</span>
          </div>
        </div>
      </Card>
      <div className="flex w-full gap-6">
        <Card className="flex flex-col basis-[428px] grow gap-6">
          <h3 className="text-lg font-semibold">Offer link</h3>
          <span
            onClick={(e) => {
              handleCopyToClipboard(e.currentTarget.innerText);
            }}
            className="flex items-center text-normal text-base gap-2"
          >
            <span className="line-clamp-1 break-all">{url}</span>{" "}
            <CopyIcon size={24} className="stroke-slate-500 min-w-6" />
          </span>
        </Card>
        {dealLink && (
          <Card className="flex flex-col basis-[664px] grow gap-6">
            <h3 className="text-lg font-semibold">Deal link</h3>
            <span
              onClick={(e) => {
                handleCopyToClipboard(e.currentTarget.innerText);
              }}
              className="flex items-center text-normal text-base gap-2"
            >
              <span className="line-clamp-1 break-all">{dealLink}</span>{" "}
              <CopyIcon size={24} className="stroke-slate-500 min-w-6" />
            </span>
          </Card>
        )}
      </div>
      <Card>
        <div className="flex justify-between items-center mb-8">
          <h3 className="text-lg font-semibold">Description</h3>
          <Button onClick={() => steps.setStep("Description")} variant="link" size="sm">
            Edit
          </Button>
        </div>
        <div className="flex gap-x-6">
          <div className="flex flex-col gap-y-6 basis-[620px] grow">
            <div className="flex flex-col gap-2 items-stretch">
              <span className="text-base font-normal">Offer title</span>
              <span className="text-base font-medium">{values.description?.title}</span>
            </div>
            <div className="flex flex-col gap-2 items-stretch">
              <span className="text-base font-normal">Intro</span>
              <span className="text-base font-medium">{values.description?.intro}</span>
            </div>
          </div>
          <Separator className="h-auto" orientation="vertical" />
          <div className="flex flex-col gap-y-6 basis-[400px] grow">
            <div className="flex flex-col gap-2 items-stretch">
              <span className="text-base font-normal">Currency</span>
              <span className="text-base font-medium">{values.description?.currency}</span>
            </div>
            <div className="flex flex-col gap-2 items-stretch">
              <span className="text-base font-normal">Charter type</span>
              <span className="text-base font-medium">{values.description?.charterType}</span>
            </div>
            <div className="flex flex-col gap-2 items-stretch">
              <span className="text-base font-normal">Enquiry date</span>
              <span className="text-base font-medium">{values.description?.enquiryDate}</span>
            </div>
            <div className="flex flex-col gap-2 items-stretch">
              <span className="text-base font-normal">Broker (Booking owner)</span>
              <span className="text-base font-medium">{values.description?.broker?.fullName}</span>
            </div>
          </div>
        </div>
      </Card>
      <Card>
        <div className="flex align-center gap-6">
          <h3 className="text-lg font-semibold">Selected yachts</h3>
          {values.description?.charterType !== "crewed" && sortedOfferYachts.length > 0 && (
            <button
              type="button"
              onClick={toggleSkipperAll}
              className={`text-sm rounded-full border px-3 py-1 ${
                someHasSkipper ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
              }`}
              title="Add an optional skipper line to every yacht"
            >
              {someHasSkipper ? "✓ Skipper added to all" : "Add skipper to all"}
            </button>
          )}
          {sortedOfferYachts.length > 0 && (
            <button
              type="button"
              onClick={toggleDiscountAll}
              className={`text-sm rounded-full border px-3 py-1 ${
                discountAllApplied ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
              }`}
              title="Reduce every option's charter fee by 5%"
            >
              {discountAllApplied ? "✓ 5% discount applied" : "Add 5% discount to all"}
            </button>
          )}
          <Button onClick={() => steps.setStep("Yachts")} className="ml-auto" variant="default">
            Add more yachts <Plus className="ml-2" size={16} />
          </Button>
          <ActionButton onClick={(e) => e.preventDefault()} eventKey="offer:openYachtsModal" variant="link">
            Edit
          </ActionButton>
        </div>
        <div className="flex flex-col gap-4 py-6">
          {sortedOfferYachts.map((offerYacht: OfferYacht, index) => {
            const p: any = offerYacht.pricing ?? {};
            const amen: any[] = (offerYacht as any).amenitiesDetails ?? [];
            const custom: any[] = (offerYacht as any).customAmenities ?? [];
            const provider = offerYacht.yacht?.provider;
            const isCya = provider === "CYA";
            const offerCurrency = values?.description?.currency ?? "EUR";
            // Locally-paid extras are stored (and paid) in the operator's local
            // currency, not the offer/advance currency — derive it from the extras'
            // own currency so they don't get mislabelled as e.g. GBP.
            const validCur = (c?: unknown) =>
              typeof c === "string" && /^[A-Z]{3}$/.test(c) ? c : undefined;
            const curOf = (a: any) => validCur(a?.currency) ?? validCur(a?.currency?.code) ?? validCur(a?.currencyCode);
            const localCurrency =
              [...amen, ...custom].filter((a) => a?.paidLocally).map(curOf).find(Boolean) ??
              curOf((offerYacht as any)?.operator) ??
              offerCurrency;
            const fmt = (n: number, paidLocally = false) =>
              new Intl.NumberFormat("en-EN", {
                style: "currency",
                currency: paidLocally ? localCurrency : offerCurrency,
                maximumFractionDigits: 0,
              }).format(n ?? 0);

            // Always-included rows: charter fee + obligatory extras.
            const fixedRows: { label: string; amount: number; paidLocally: boolean; obligatory: boolean }[] = [
              { label: "Charter fee", amount: p.clientPrice ?? p.totalPrice ?? 0, paidLocally: false, obligatory: false },
              ...(p.advanceObligatory ?? []).map((a: any) => ({ label: a.text, amount: a.price, paidLocally: false, obligatory: true })),
              ...(p.pickupObligatory ?? []).map((a: any) => ({ label: a.text, amount: a.price, paidLocally: true, obligatory: true })),
            ];

            // Toggleable optional extras (from the yacht's full amenity list +
            // any custom lines). `checked` drives inclusion; ticking recomputes
            // the advance total.
            type Extra = {
              source: "amen" | "custom";
              origIndex: number;
              label: string;
              amount: number;
              paidLocally: boolean;
              checked: boolean;
            };
            const extras: Extra[] = [
              ...amen
                .map((a, origIndex) => ({ a, origIndex }))
                .filter(({ a }) => !a.obligatory && a.price)
                .map(({ a, origIndex }) => ({
                  source: "amen" as const,
                  origIndex,
                  label: a.text ?? "Extra",
                  amount: a.price ?? 0,
                  paidLocally: !!a.paidLocally,
                  checked: !!a.checked,
                })),
              ...custom.map((a, origIndex) => ({
                source: "custom" as const,
                origIndex,
                label: a.text ?? "Extra",
                amount: a.price ?? 0,
                paidLocally: !!a.paidLocally,
                checked: a.checked !== false,
              })),
            ];
            const expanded = !!expandedExtras[index];
            const shownExtras = expanded ? extras : extras.slice(0, 5);

            const toggleExtra = (item: Extra) => {
              const nextChecked = !item.checked;
              const amenitiesDetails = [...amen];
              const customAmenities = [...custom];
              if (item.source === "amen")
                amenitiesDetails[item.origIndex] = { ...amenitiesDetails[item.origIndex], checked: nextChecked };
              else customAmenities[item.origIndex] = { ...customAmenities[item.origIndex], checked: nextChecked };
              // Advance extras move the advance total; locally-paid ones don't.
              const delta = item.paidLocally ? 0 : nextChecked ? item.amount : -item.amount;
              const pricing = { ...p, totalPrice: (p.totalPrice ?? 0) + delta };
              updateLine(index, { amenitiesDetails, customAmenities, pricing });
            };

            const whenLabel = (paidLocally: boolean) => (paidLocally ? "Paid locally" : "In advance");
            const paymentWhen = (s: any) =>
              s.type === "on confirmation"
                ? "On confirmation"
                : s.type === "before booking date"
                  ? `${s.days} days before charter`
                  : `${s.days} days after confirmation`;
            const reaction = (offerYacht as any).reaction as "love" | "maybe" | "pass" | null | undefined;
            const reactionNote = (offerYacht as any).reactionNote as string | null | undefined;
            const reactionStyle: Record<string, string> = {
              love: "bg-rose-50 border-rose-200 text-rose-800",
              maybe: "bg-amber-50 border-amber-200 text-amber-800",
              pass: "bg-slate-100 border-slate-200 text-slate-700",
            };
            const reactionLabel: Record<string, string> = {
              love: "❤ Client loves this",
              maybe: "◐ Client is unsure",
              pass: "✗ Client passed on this",
            };

            return (
              <div key={offerYacht.id || index} className="flex flex-col border rounded-lg border-blue-100 overflow-hidden">
                {/* Client feedback sits above the yacht so it's read before deciding what to
                    change — reactions/notes come back from the client offer page. */}
                {!!reaction && (
                  <div
                    className={`flex items-start gap-2 px-4 py-2 text-sm border-b ${reactionStyle[reaction] ?? reactionStyle.pass}`}
                  >
                    <span className="font-semibold shrink-0">{reactionLabel[reaction] ?? "Client feedback"}</span>
                    {!!reactionNote && <span className="font-normal">— “{reactionNote}”</span>}
                  </div>
                )}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-blue-50 bg-white">
                  <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center shrink-0">
                    {index + 1}
                  </span>
                  <span className="font-semibold">{offerYacht.yacht?.name}</span>
                  <span className="text-sm text-slate-500 truncate">
                    {[offerYacht.yacht?.builder, offerYacht.yacht?.boatType].filter(Boolean).join(" · ")}
                  </span>
                  {(offerYacht as any).availabilityChecked && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 text-xs font-medium"
                      title={
                        (offerYacht as any).checkedAt
                          ? `Live price & availability checked ${new Date((offerYacht as any).checkedAt).toLocaleString()}`
                          : "Live price & availability checked in the background"
                      }
                    >
                      <Check size={12} /> Price &amp; availability checked
                    </span>
                  )}
                  <span className="ml-auto flex items-center gap-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => moveYacht(index, index - 1)}>
                      <ArrowUp size={15} />
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => moveYacht(index, index + 1)}>
                      <ArrowDown size={15} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => openChangeDetails(offerYacht.yacht.id, index)}
                    >
                      Edit details
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(index)}>
                      Remove
                    </Button>
                  </span>
                </div>
                {(() => {
                  const oy: any = offerYacht;
                  const fmtDT = (d?: string, t?: string) => {
                    if (!d) return "";
                    const dt = new Date(d);
                    const date = isNaN(dt.getTime())
                      ? d
                      : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
                    return t ? `${date} · ${t}` : date;
                  };
                  const ci = fmtDT(oy.startDate, oy.startTime);
                  const co = fmtDT(oy.endDate, oy.endTime);
                  if (!ci && !co && !oy.startLocation?.names && !oy.endLocation?.names) return null;
                  return (
                    <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1 px-4 py-2 border-b border-blue-50 bg-slate-50/40 text-xs">
                      <span className="truncate">
                        <span className="text-slate-400">Check-in </span>
                        <b>{oy.startLocation?.names || "—"}</b>
                        {ci ? ` · ${ci}` : ""}
                      </span>
                      <span className="truncate">
                        <span className="text-slate-400">Check-out </span>
                        <b>{oy.endLocation?.names || "—"}</b>
                        {co ? ` · ${co}` : ""}
                      </span>
                    </div>
                  );
                })()}
                {isCya &&
                  (() => {
                    // In CYA terms "inclusive" describes the CHARTER STYLE (all-inclusive
                    // — crew, food, fuel, dockage), NOT that VAT is included. "Exclusive"
                    // (a.k.a. plus-expenses) means the client also pays running costs via
                    // an APA. VAT is a separate line shown whenever the feed reports it.
                    const inclusiveStyle = p.charterTerms !== "exclusive";
                    const brochure = (offerYacht.yacht as any)?.meta?.brochureLink;
                    return (
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 border-b border-blue-50 bg-blue-50/40 text-xs">
                        <span className="font-medium text-slate-600">
                          {inclusiveStyle
                            ? "All-inclusive charter (crew, food, fuel & dockage)"
                            : "Plus expenses — APA payable on top"}
                        </span>
                        {!!p.vatPrice && (
                          <span className="text-slate-600">
                            + VAT{p.vatPercentage ? ` (${p.vatPercentage}%)` : ""}: <b>{fmt(p.vatPrice)}</b>
                          </span>
                        )}
                        {!!p.apaPrice && (
                          <span className="text-slate-600">
                            + APA{p.apaPercentage ? ` (${p.apaPercentage}%)` : ""}: <b>{fmt(p.apaPrice)}</b>
                          </span>
                        )}
                        {(!!p.vatPrice || !!p.apaPrice) && (
                          <span className="text-slate-700 font-semibold">
                            All-in est.{" "}
                            {fmt((p.clientPrice ?? p.totalPrice ?? 0) + (p.vatPrice ?? 0) + (p.apaPrice ?? 0))}
                          </span>
                        )}
                        <span className="ml-auto flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => setRatesFor(offerYacht)}
                            className="text-blue-600 hover:underline font-medium"
                          >
                            Rates &amp; availability
                          </button>
                          {brochure && (
                            <a
                              href={brochure}
                              target="_blank"
                              rel="noreferrer"
                              className="text-blue-600 hover:underline font-medium inline-flex items-center gap-1"
                            >
                              CYA brochure <ExternalLink size={12} />
                            </a>
                          )}
                        </span>
                      </div>
                    );
                  })()}
                {!isCya &&
                  (() => {
                    // Non-CYA crewed yachts (e.g. Greek Yachtfolio): surface VAT + APA
                    // and an estimated all-in. Percentages come from the pricing
                    // breakdown or, if absent, the yacht's seasonal rate meta.
                    const rateInfo: any = (offerYacht.yacht as any)?.meta?.generalRates?.[0]?.priceInfo;
                    const vatPct = p.vatPercentage ? Number(p.vatPercentage) : (rateInfo?.vat ?? 0);
                    const apaPct = p.apaPercentage ? Number(p.apaPercentage) : (rateInfo?.apa ?? 0);
                    const inclusive =
                      p.charterTerms === "inclusive" || (offerYacht.yacht as any)?.meta?.cyaPricing?.inclusive;
                    if (inclusive || (vatPct <= 0 && apaPct <= 0)) return null;
                    const base = p.clientPrice ?? p.totalPrice ?? 0;
                    const vatAmt = p.vatPrice || (base * vatPct) / 100;
                    const apaAmt = p.apaPrice || (base * apaPct) / 100;
                    return (
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 border-b border-blue-50 bg-blue-50/40 text-xs">
                        <span className="font-medium text-slate-600">Plus expenses:</span>
                        {vatPct > 0 && (
                          <span className="text-slate-600">
                            + VAT ({vatPct}%): <b>{fmt(vatAmt)}</b>
                          </span>
                        )}
                        {apaPct > 0 && (
                          <span className="text-slate-600">
                            + APA ({apaPct}%): <b>{fmt(apaAmt)}</b>
                          </span>
                        )}
                        <span className="ml-auto text-slate-700 font-semibold">
                          All-in est. {fmt(base + vatAmt + apaAmt)}
                        </span>
                      </div>
                    );
                  })()}
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 border-b border-blue-50">
                      <th className="py-2 px-4 font-medium">Line item</th>
                      <th className="py-2 px-4 font-medium">When</th>
                      <th className="py-2 px-4 font-medium text-right">Amount</th>
                      <th className="py-2 px-4 font-medium text-center">Include</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fixedRows.map((r, ri) => (
                      <tr key={`f${ri}`} className="border-b border-blue-50">
                        <td className="py-2 px-4">{r.label}</td>
                        <td className="py-2 px-4 text-xs text-slate-500">{whenLabel(r.paidLocally)}</td>
                        <td className="py-2 px-4 text-right font-medium whitespace-nowrap">
                          {ri === 0 ? (
                            <span className="inline-flex items-center gap-1 justify-end">
                              <span className="text-slate-400">{values?.description?.currency ?? "EUR"}</span>
                              <input
                                key={`price-${Math.round(r.amount)}`}
                                type="number"
                                defaultValue={Math.round(r.amount)}
                                onBlur={(e) => setCustomerPrice(index, p, Number(e.target.value) || 0)}
                                className="w-28 text-right border border-blue-200 rounded px-2 py-1 text-sm font-medium bg-white"
                                title="Customer charter price — editable"
                              />
                              {discountAllApplied && (
                                <span
                                  className="ml-1 text-xs font-medium text-blue-600 whitespace-nowrap"
                                  title="A 5% HELM discount has been applied to this charter fee"
                                >
                                  −5% HELM
                                </span>
                              )}
                            </span>
                          ) : (
                            fmt(r.amount, r.paidLocally)
                          )}
                        </td>
                        <td className="py-2 px-4 text-center">
                          <span
                            className={`inline-flex py-0.5 px-2 rounded-full text-xs font-medium ${
                              r.obligatory ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"
                            }`}
                          >
                            {r.obligatory ? "Obligatory" : "Included"}
                          </span>
                        </td>
                      </tr>
                    ))}
                    {shownExtras.map((r) => (
                      <tr
                        key={`${r.source}-${r.origIndex}`}
                        className={`border-b border-blue-50 last:border-b-0 ${r.checked ? "" : "opacity-60"}`}
                      >
                        <td className="py-2 px-4">{r.label}</td>
                        <td className="py-2 px-4 text-xs text-slate-500">{whenLabel(r.paidLocally)}</td>
                        <td className="py-2 px-4 text-right font-medium whitespace-nowrap">{fmt(r.amount, r.paidLocally)}</td>
                        <td className="py-2 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={r.checked}
                            onChange={() => toggleExtra(r)}
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 cursor-pointer accent-blue-600"
                            aria-label={`Include ${r.label}`}
                          />
                        </td>
                      </tr>
                    ))}
                    {extras.length > 5 && (
                      <tr>
                        <td colSpan={4} className="py-2 px-4">
                          <button
                            type="button"
                            onClick={() => setExpandedExtras((s) => ({ ...s, [index]: !expanded }))}
                            className="text-xs font-medium text-blue-600 hover:underline"
                          >
                            {expanded ? "Show fewer extras" : `View all ${extras.length} extras`}
                          </button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {!!offerYacht.paymentSteps?.length && (
                  <div className="px-4 py-3 border-t border-blue-50 bg-white">
                    <div className="text-xs font-medium text-slate-500 mb-1.5">Payment schedule</div>
                    <div className="flex flex-col gap-1">
                      {offerYacht.paymentSteps.map((s: any, si: number) => (
                        <div key={si} className="flex justify-between text-sm">
                          <span className="text-slate-600">{paymentWhen(s)}</span>
                          <span className="font-medium whitespace-nowrap">{fmt(s.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex flex-col gap-3 px-4 py-3 border-t border-blue-50 bg-slate-50/60">
                  <div className="flex items-center gap-2">
                    <input
                      key={`bn-${index}-${(offerYacht as any).brokerNote ?? ""}`}
                      className="w-full border rounded-md p-2 text-sm border-blue-100 bg-white"
                      defaultValue={(offerYacht as any).brokerNote ?? ""}
                      onBlur={(e) => updateLine(index, { brokerNote: e.target.value })}
                      placeholder="Broker note for this yacht (why it fits)…"
                    />
                    <button
                      type="button"
                      onClick={() => draftAi("yacht", index, offerYacht)}
                      disabled={drafting === index}
                      className="shrink-0 text-xs font-medium text-blue-600 hover:underline disabled:text-slate-400 inline-flex items-center gap-1 whitespace-nowrap"
                    >
                      <Sparkles size={13} /> {drafting === index ? "…" : "Draft with AI"}
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!(offerYacht as any).ignoreAvailability}
                        onChange={(e) => updateLine(index, { ignoreAvailability: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300 accent-blue-600 cursor-pointer"
                      />
                      Ignore availability
                    </label>
                    {(offerYacht as any).yachtOnHold || (offerYacht as any).holdExpiresAt ? (
                      <span className="text-sm font-medium text-emerald-700 inline-flex items-center gap-1">
                        <Check size={14} /> On hold
                        {(offerYacht as any).holdExpiresAt
                          ? ` until ${new Date((offerYacht as any).holdExpiresAt).toLocaleDateString("en-GB")}`
                          : ""}
                        <button
                          type="button"
                          onClick={() => updateLine(index, { yachtOnHold: false, holdExpiresAt: null })}
                          className="ml-1 text-xs text-slate-400 hover:text-slate-600 underline"
                        >
                          clear
                        </button>
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={holding === index}
                        onClick={() => putOnHold(index, offerYacht)}
                      >
                        {holding === index ? "Putting on hold…" : "Put on hold for client"}
                      </Button>
                    )}
                    <span className="ml-auto font-semibold whitespace-nowrap text-right">{fmt(p.totalPrice ?? 0)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
      <YachtDetailsModal yachtDetailsId={yachtId} open={yachtDetailsOpen} setOpen={setYachtDetailsOpen} />
      <CyaRatesDialog
        offerYacht={ratesFor}
        onClose={() => setRatesFor(null)}
        currency={values?.description?.currency ?? "EUR"}
      />
    </div>
    {/* Overall message rail (prototype builderConfig): the covering note that
        heads the client's offer page, plus a glance at hold state. */}
    <aside className="xl:sticky xl:top-4 flex flex-col gap-4">
      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold">Message to {firstName || "the client"}</h3>
          <button
            type="button"
            onClick={() => draftAi("message")}
            disabled={drafting === "message"}
            className="text-xs font-medium text-blue-600 hover:underline disabled:text-slate-400 inline-flex items-center gap-1"
          >
            <Sparkles size={13} /> {drafting === "message" ? "Drafting…" : "Draft with AI"}
          </button>
        </div>
        <textarea
          className="w-full border rounded-md border-blue-100 p-3 text-sm min-h-[130px]"
          placeholder="Personal note that heads the offer…"
          value={intro}
          onChange={(e) => {
            setIntro(e.target.value);
            setValue("description.intro", e.target.value, { shouldDirty: true });
          }}
        />
        <p className="text-xs text-slate-400 mt-2">
          This heads the offer the client opens — write it to them, not about them.
        </p>
      </Card>
      <Card>
        <div className="flex items-center justify-between text-sm py-1">
          <span className="text-slate-500">Reactions</span>
          <span className="font-medium">❤ / maybe / ✗ enabled</span>
        </div>
        <Button
          type="button"
          className="w-full mt-3"
          onClick={() => (offerId ? steps.setStep("Preview") : emitPreviewSave(undefined))}
        >
          {offerId ? "Preview offer →" : "Save draft & preview →"}
        </Button>
      </Card>
      <Card>
        <h3 className="text-lg font-semibold mb-2">Holds</h3>
        <p className="text-sm text-slate-500">
          {heldCount
            ? `${heldCount} yacht${heldCount === 1 ? "" : "s"} with a hold window set — the client sees "Held for you until…" on those.`
            : "No hold windows set. Add one per yacht below its card to show the client a reserved-until date."}
        </p>
      </Card>
    </aside>
    </div>
  );
}

// CYA rates & availability: surfaces what the CYA API pulls — the seasonal rate
// table (yacht meta) plus the live availability calendar (booked vs free weeks
// with prices) from the search service's /availability endpoint.
function CyaRatesDialog({
  offerYacht,
  onClose,
  currency,
}: {
  offerYacht: OfferYacht | null;
  onClose: () => void;
  currency: string;
}) {
  const [periods, setPeriods] = useState<Availability[] | null>(null);
  const [loading, setLoading] = useState(false);
  const yachtId = offerYacht?.yacht?.id;
  const meta: any = (offerYacht?.yacht as any)?.meta ?? {};
  const generalRates: any[] = meta.generalRates ?? [];
  const perGuestRates: any[] = meta.perGuestRates ?? [];
  const rateText: string | undefined = meta.cyaPricing?.details;
  const baseLoc: string | undefined = (offerYacht as any)?.startLocation?.names;
  const endLoc: string | undefined = (offerYacht as any)?.endLocation?.names;

  useEffect(() => {
    if (!yachtId) {
      setPeriods(null);
      return;
    }
    setLoading(true);
    fetchAvailability(yachtId)
      .then((r) => setPeriods(Array.isArray(r) ? r : []))
      .catch(() => setPeriods([]))
      .finally(() => setLoading(false));
  }, [yachtId]);

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-EN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n ?? 0);
  const fmtDate = (s: string) => {
    try {
      return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return s;
    }
  };
  const sorted = [...(periods ?? [])].sort(
    (a, b) => new Date(a.periodStart).getTime() - new Date(b.periodStart).getTime(),
  );

  return (
    <Dialog open={!!offerYacht} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Rates &amp; availability — {offerYacht?.yacht?.name}</DialogTitle>
        </DialogHeader>
        {(baseLoc || endLoc) && (
          <div className="text-sm text-slate-600 mb-1">
            <span className="text-slate-400">Base: </span>
            {baseLoc || "—"}
            {endLoc && endLoc !== baseLoc ? ` → ${endLoc}` : ""}
          </div>
        )}
        {rateText && (
          <div className="mb-3">
            <div className="text-xs font-medium text-slate-500 mb-1">Rate information (from CYA)</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{rateText}</p>
          </div>
        )}
        {!!generalRates.length && (
          <div className="mb-3">
            <div className="text-xs font-medium text-slate-500 mb-1">Seasonal rates (from CYA)</div>
            <table className="w-full text-sm">
              <tbody>
                {generalRates.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 last:border-0">
                    <td className="py-1.5">
                      {fmtDate(r.from)} – {fmtDate(r.to)}
                      {r.locations?.length ? <span className="text-slate-400"> · {r.locations.join(", ")}</span> : null}
                    </td>
                    <td className="py-1.5 text-right font-medium whitespace-nowrap">
                      {r.priceInfo?.price
                        ? `${fmt(r.priceInfo.price)}${r.priceInfo.per ? ` / ${r.priceInfo.per}` : ""}`
                        : "—"}
                      {r.priceInfo?.vat ? <span className="text-slate-400"> +VAT</span> : null}
                      {r.priceInfo?.apa ? <span className="text-slate-400"> +APA</span> : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!!perGuestRates.length && (
          <div className="mb-3">
            <div className="text-xs font-medium text-slate-500 mb-1">Per-guest rates (from CYA)</div>
            {perGuestRates.map((s: any, si: number) => (
              <div key={si} className="mb-2">
                <div className="text-xs text-slate-500">
                  {s.seasonName}
                  {s.terms ? ` · ${s.terms}` : ""}
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {(s.items ?? []).map((it: any, ii: number) => (
                      <tr key={ii} className="border-b border-slate-100 last:border-0">
                        <td className="py-1">{it.guests} guests</td>
                        <td className="py-1 text-right font-medium">{it.rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
        <div className="text-xs font-medium text-slate-500 mb-1">Availability{loading ? " (loading…)" : ""}</div>
        {!loading && !sorted.length && (
          <p className="text-sm text-slate-500">No availability data pulled for this yacht.</p>
        )}
        {!!sorted.length && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                <th className="py-1.5 font-medium">Week</th>
                <th className="py-1.5 font-medium text-right">Price</th>
                <th className="py-1.5 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((pd, i) => (
                <tr key={i} className="border-b border-slate-100 last:border-0">
                  <td className="py-1.5">
                    {fmtDate(pd.periodStart)} – {fmtDate(pd.periodEnd)}
                  </td>
                  <td className="py-1.5 text-right font-medium">{pd.clientPrice ? fmt(pd.clientPrice) : "—"}</td>
                  <td className="py-1.5 text-right">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        pd.available ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                      }`}
                    >
                      {pd.available ? "Available" : "Booked"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}
